import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { loadStripe } from '@stripe/stripe-js';

// Stripe API key
const STRIPE_KEY = "pk_live_51IuUXbLwpaCHfpZ1EZNNYaQYdXSIrE6NrdA3r7lUQOIq5tsdhC7MaTITIMFb3nW4yxqyppRd8C66QcxWhuoPC8aa00Ow2q0o0z";

// Debug logging
console.log('StripeContext file is loaded');
console.log('Stripe key type:', typeof STRIPE_KEY);
console.log('Stripe key length:', STRIPE_KEY.length);
console.log('Stripe key first 10 chars:', STRIPE_KEY.substring(0, 10));

// Check if Stripe is available globally
console.log('Global Stripe object exists:', typeof window !== 'undefined' && !!window.Stripe);

const StripeContext = createContext();

export function StripeProvider({ children }) {
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState({
    hasStripeAccount: false,
    onboardingComplete: false,
    chargesEnabled: false,
    payoutsEnabled: false,
    detailsSubmitted: false
  });
  const auth = getAuth();
  
  console.log('StripeProvider rendered');

  // Initialize Stripe when the context loads
  useEffect(() => {
    console.log('StripeProvider useEffect - initializing Stripe');
    const initializeStripe = async () => {
      try {
        console.log('Loading Stripe with key:', STRIPE_KEY);
        const stripe = await loadStripe(STRIPE_KEY);
        console.log('Stripe instance created:', !!stripe);
        setStripeInstance(stripe);
      } catch (error) {
        console.error('Error creating Stripe instance:', error);
      }
    };

    initializeStripe();
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserId(user.uid);
        checkStripeStatus(user.uid);
      } else {
        setUserId(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth]);

  // Periodic status checking - check status when window gains focus or every 5 minutes
  useEffect(() => {
    if (!userId) return;

    const handleFocus = () => {
      console.log('Window focused, checking Stripe status...');
      checkStripeStatus(userId);
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page visible, checking Stripe status...');
        checkStripeStatus(userId);
      }
    };

    // Check immediately when user is set
    checkStripeStatus(userId);

    // Check when window gains focus
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic check every 5 minutes for incomplete onboarding
    const intervalId = setInterval(() => {
      if (!onboardingStatus.onboarding_complete && onboardingStatus.hasStripeAccount) {
        console.log('Periodic check for incomplete onboarding...');
        checkStripeStatus(userId);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [userId, onboardingStatus.onboarding_complete, onboardingStatus.hasStripeAccount]);

  const checkStripeStatus = async (uid) => {
    try {
      // Use the new backend endpoint for comprehensive status checking
      const response = await fetch(`https://klorah-fast-server-my8l.onrender.com/api/user-onboarding-status/${uid}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const statusData = await response.json();
      
      // Update all status states
      setOnboardingStatus(statusData);
      setStripeAccountId(statusData.account_id || null);
      
      // Only mark as onboarded if they have completed ALL requirements
      setIsOnboarded(statusData.charges_enabled && statusData.payouts_enabled);
      
      console.log('Stripe status updated:', statusData);
      
      return statusData;
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      
      // Fallback to Firestore if backend fails
      try {
        const firestore = getFirestore();
        const userRef = doc(firestore, 'users', uid);
        const profileRef = doc(firestore, 'profiles', uid);
        
        const [userDoc, profileDoc] = await Promise.all([
          getDoc(userRef),
          getDoc(profileRef)
        ]);
        
        const userData = userDoc.exists() ? userDoc.data() : {};
        const profileData = profileDoc.exists() ? profileDoc.data() : {};
        
        const hasStripeAccount = userData.stripeAccountId || profileData.stripeConnectedAccountId;
        const isComplete = userData.onboardingComplete || profileData.onboardingComplete;
        
        setStripeAccountId(userData.stripeAccountId || profileData.stripeConnectedAccountId || null);
        
        // Only consider truly onboarded if explicitly marked complete and has account
        setIsOnboarded(!!hasStripeAccount && !!isComplete);
        
        setOnboardingStatus({
          hasStripeAccount: !!hasStripeAccount,
          onboardingComplete: !!isComplete,
          chargesEnabled: userData.chargesEnabled || false,
          payoutsEnabled: userData.payoutsEnabled || false,
          detailsSubmitted: userData.detailsSubmitted || false
        });
        
        return {
          hasStripeAccount: !!hasStripeAccount,
          onboardingComplete: !!isComplete
        };
      } catch (firestoreError) {
        console.error('Firestore fallback also failed:', firestoreError);
        return null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startOnboarding = async () => {
    if (!userId) throw new Error('User must be logged in to start onboarding');
    
    // Check email verification first (required for Stripe onboarding)
    if (!auth.currentUser.emailVerified) {
      const error = new Error('Email verification is required before Stripe onboarding. Please verify your email first.');
      error.code = 'auth/unverified-email';
      error.requiresEmailVerification = true;
      throw error;
    }
    
    try {
      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/create-onboarding-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred during onboarding');
      }
      
      if (!data.url) {
        throw new Error('No URL returned from the server');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Detailed onboarding error:', error);
      throw new Error(error.message || 'Error during onboarding. Please try again later.');
    }
  };

  // Function to start onboarding without email verification (bypass)
  const startOnboardingWithoutEmailVerification = async () => {
    if (!userId) throw new Error('User must be logged in to start onboarding');
    
    try {
      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/create-onboarding-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred during onboarding');
      }
      
      if (!data.url) {
        throw new Error('No URL returned from the server');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Detailed onboarding error:', error);
      throw new Error(error.message || 'Error during onboarding. Please try again later.');
    }
  };

  // Function to get the Stripe instance
  const getStripe = () => {
    console.log('getStripe called, instance exists:', !!stripeInstance);
    
    if (!stripeInstance) {
      console.log('No Stripe instance available - creating one now');
      return loadStripe(STRIPE_KEY)
        .then(newStripe => {
          console.log('Created new Stripe instance in getStripe:', !!newStripe);
          setStripeInstance(newStripe);
          return newStripe;
        })
        .catch(error => {
          console.error('Error creating Stripe instance in getStripe:', error);
          return null;
        });
    }
    
    return Promise.resolve(stripeInstance);
  };



  // Function to check onboarding status
  const checkOnboardingStatus = async () => {
    if (!userId) throw new Error('User must be logged in to check onboarding status');
    
    try {
      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/check-onboarding-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'An error occurred checking onboarding status');
      }
      
      // Update local state
      setIsOnboarded(data.onboarding_complete);
      
      return data;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      throw new Error(error.message || 'Error checking onboarding status.');
    }
  };

  // Function to handle checkout redirects
  const redirectToCheckout = async (sessionId) => {
    console.log('redirectToCheckout called with sessionId:', sessionId);
    try {
      console.log('Getting Stripe instance for checkout');
      const stripe = await getStripe();
      console.log('Stripe instance received:', !!stripe);
      
      if (!stripe) {
        console.error('Stripe instance is null');
        throw new Error('Failed to initialize Stripe');
      }
      
      console.log('Calling stripe.redirectToCheckout');
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        throw new Error(error.message || 'Failed to redirect to checkout');
      }
      console.log('redirectToCheckout called successfully');
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  };

  const value = {
    isOnboarded,
    stripeAccountId,
    isLoading,
    onboardingStatus,
    startOnboarding,
    startOnboardingWithoutEmailVerification,
    checkOnboardingStatus,
    redirectToCheckout,
    getStripe,
    checkStripeStatus: () => checkStripeStatus(userId),
    refreshStatus: () => userId ? checkStripeStatus(userId) : null,
  };

  return (
    <StripeContext.Provider value={value}>
      {children}
    </StripeContext.Provider>
  );
}

export function useStripe() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}
